// Message Service
// Thin client wrapper around callable messaging backend.

import { auth } from '../firebase.js';
import {
  callMessagingFunction,
  normalizeConversation,
  normalizeMessage,
} from './callableMessagingClient.js';

const requireCurrentUser = (userId) => {
  const currentUserId = auth.currentUser?.uid || null;
  if (!currentUserId) {
    return {
      success: false,
      error: 'Authentication required',
      code: 'unauthenticated',
    };
  }

  if (userId && currentUserId !== userId) {
    return {
      success: false,
      error: 'Sender identity does not match the signed-in user',
      code: 'permission-denied',
    };
  }

  return { success: true, currentUserId };
};

export const sendMessage = async ({
  conversationId,
  senderId,
  senderName,
  text,
  type = 'text',
  attachments = [],
  metadata = {},
  priority = 'normal',
  replyTo = null,
  childId = null,
  clientMessageId = null,
}) => {
  try {
    const authCheck = requireCurrentUser(senderId);
    if (!authCheck.success) return authCheck;

    if (type === 'system') {
      return {
        success: false,
        error: 'System messages must be created by trusted backend logic',
      };
    }

    if (!conversationId) {
      return {
        success: false,
        error: 'Conversation ID is required',
      };
    }

    const result = await callMessagingFunction('sendConversationMessage', {
      conversationId,
      senderId,
      senderName,
      text,
      type,
      attachments,
      metadata,
      priority,
      replyTo,
      childId,
      clientMessageId,
    });

    return {
      success: true,
      messageId: result.messageId || null,
      message: result.message || 'Message sent successfully',
    };
  } catch (error) {
    console.error('Error in sendMessage:', error);
    return {
      success: false,
      error: error.message || 'Failed to send message',
    };
  }
};

export const getMessages = async (conversationId, userId, options = {}) => {
  try {
    const authCheck = requireCurrentUser(userId);
    if (!authCheck.success) return authCheck;

    if (!conversationId) {
      return {
        success: false,
        error: 'Conversation ID is required',
      };
    }

    const result = await callMessagingFunction('getConversationMessages', {
      conversationId,
      limit: Number(options.limit) || 50,
      includeDeleted: Boolean(options.includeDeleted),
    });

    const messages = Array.isArray(result.messages)
      ? result.messages.map((message) => normalizeMessage(message)).filter(Boolean)
      : [];

    return {
      success: true,
      conversation: normalizeConversation(result.conversation),
      messages,
      totalCount: messages.length,
      hasMore: messages.length === (Number(options.limit) || 50),
      message: result.message || `Retrieved ${messages.length} messages`,
    };
  } catch (error) {
    console.error('Error in getMessages:', error);
    return {
      success: false,
      error: error.message || 'Failed to get messages',
      code: error.code || 'unknown',
    };
  }
};

export const updateMessage = async (messageId, updates, userId) => {
  try {
    const authCheck = requireCurrentUser(userId);
    if (!authCheck.success) return authCheck;
    return {
      success: false,
      error: 'Message editing is not enabled in the callable messaging backend yet',
    };
  } catch (error) {
    console.error('Error in updateMessage:', error);
    return {
      success: false,
      error: error.message || 'Failed to update message',
    };
  }
};

export const deleteMessage = async (messageId, userId) => {
  try {
    const authCheck = requireCurrentUser(userId);
    if (!authCheck.success) return authCheck;
    return {
      success: false,
      error: 'Message deletion is not enabled in the callable messaging backend yet',
    };
  } catch (error) {
    console.error('Error in deleteMessage:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete message',
    };
  }
};

export const markMessageAsRead = async (messageId, userId) => {
  try {
    const authCheck = requireCurrentUser(userId);
    if (!authCheck.success) return authCheck;

    if (!messageId) {
      return {
        success: false,
        error: 'Message ID is required',
      };
    }

    const result = await callMessagingFunction('markConversationMessageRead', {
      messageId,
    });

    return {
      success: Boolean(result.success !== false),
      message: result.message || 'Message marked as read successfully',
    };
  } catch (error) {
    console.error('Error in markMessageAsRead:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark message as read',
    };
  }
};

export const getUnreadMessages = async (userId, _childId = null) => {
  try {
    const authCheck = requireCurrentUser(userId);
    if (!authCheck.success) return authCheck;
    return {
      success: true,
      unreadMessages: [],
      totalCount: 0,
      message: 'Unread messages are not queried separately in the callable backend',
    };
  } catch (error) {
    console.error('Error in getUnreadMessages:', error);
    return {
      success: false,
      error: error.message || 'Failed to get unread messages',
    };
  }
};
